import { useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import ReactMarkdown from "react-markdown";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import NoDataImage from "assets/images/no-data2.svg";
import MedicalScribeFeedbackButtons from "features/medical-scribe/components/medical-scribe-feedback-buttons.component";
import {
  feedbackSoapNoteType,
  feedbackSummaryType,
  feedbackTranscriptType
} from "features/medical-scribe/constants";
import { getScribeModalPopoverZIndex } from "features/medical-scribe/medical-scribe-constants";
import MarkdownContainer from "features/patients/sections/patient-ai-summary/components/markdown-container.component";
import useCopyHtmlContentToClipboard from "features/patients/sections/patient-chart-notes/hooks/use-copy-html-content-to-clipboard.hook";
import { isLikelyHtml } from "utils/markdown.utils";

import GenerateScribeSection from "../generate-scribe-section.component";

type TextTabProps = {
  id: number;
  value?: string;
  type: "soap" | "summary";
  /** True while the initial RTK Query fetch is in-flight — keeps generate button disabled */
  isLoading?: boolean;
  /** Combined truth: localGenerating (parent) OR remote.is_generating_* (server) */
  isGenerating: boolean;
  /** Orchestrator handles mutation + paywall check + localGenerating state */
  onGenerate: () => void;
  /**
   * Passed through to GenerateScribeSection (soap only) so the async regenerate path
   * also notifies the parent. Optional — summary has no GenerateScribeSection.
   */
  onGenerateStarted?: () => void;
  scribeTemplateId?: number | null;
};

export default function TextTab({
  id,
  value,
  type,
  isLoading,
  isGenerating,
  onGenerate,
  onGenerateStarted = () => {},
  scribeTemplateId
}: Readonly<TextTabProps>) {
  const theme = useTheme();
  const tooltipPopperProps = useMemo(
    () => ({
      style: { zIndex: getScribeModalPopoverZIndex(theme.zIndex.snackbar) }
    }),
    [theme.zIndex.snackbar]
  );
  const isSoap = type === "soap";
  const isSummary = type === "summary";
  const feedbackType = isSummary
    ? feedbackSummaryType
    : isSoap
      ? feedbackSoapNoteType
      : feedbackTranscriptType;

  const [showCopied, setShowCopied] = useState(false);
  const copyHtmlContent = useCopyHtmlContentToClipboard();
  const treatAsHtml = isLikelyHtml(value);

  const handleCopyText = async () => {
    try {
      const content = treatAsHtml
        ? (value ?? "")
        : renderToStaticMarkup(<ReactMarkdown>{value}</ReactMarkdown>);
      await copyHtmlContent(content);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  // Case 1: content exists (with or without regeneration in progress)
  if (value) {
    return (
      <Box width="100%" mt={2}>
        {/*
          Toolbar — 3 permanent sections so layout is stable even when center or
          right children return null (e.g. MedicalScribeFeedbackButtons returns
          null during polling refetches).
          Left: copy | Center: regenerate (soap only) | Right: feedback
        */}
        <Stack
          mb={2}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          {/* Left — copy button, always present */}
          <Stack direction="row" alignItems="center" gap={1} flexShrink={0}>
            <Tooltip
              title="Copy text"
              arrow
              disableInteractive
              PopperProps={tooltipPopperProps}
            >
              <IconButton
                onClick={handleCopyText}
                color="primary"
                sx={{ border: "1px solid", borderColor: "divider" }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {showCopied && (
              <Typography variant="caption" color="success.main">
                Copied!
              </Typography>
            )}
          </Stack>

          {/* Center — regenerate controls, soap only */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ flex: 1, mx: 1 }}
          >
            {isSoap && (
              <GenerateScribeSection
                scribeId={id}
                isGeneratingSoap={isGenerating}
                scribeTemplateId={scribeTemplateId}
                onGenerateStarted={onGenerateStarted}
              />
            )}
          </Stack>

          {/* Right — feedback buttons, always present as a slot */}
          <Stack direction="row" alignItems="center" flexShrink={0}>
            <MedicalScribeFeedbackButtons
              scribeId={id}
              feedbackType={feedbackType}
            />
          </Stack>
        </Stack>

        <MarkdownContainer html={treatAsHtml}>{value}</MarkdownContainer>
      </Box>
    );
  }

  // Case 2 & 3 (soap): GenerateScribeSection owns both idle and generating states
  if (isSoap && !isGenerating) {
    return (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        columnGap={2.5}
        alignItems="center"
        justifyContent="center"
        width="100%"
        minHeight="280px"
        mt={2}
      >
        <Box
          component="img"
          src={NoDataImage}
          alt=""
          sx={{ height: "130px", p: 2 }}
        />
        <Stack rowGap={3}>
          <Typography variant="body3">
            No Scribe available yet. Click the button to generate.
          </Typography>
          <GenerateScribeSection
            scribeId={id}
            isGeneratingSoap={isGenerating}
            scribeTemplateId={scribeTemplateId}
            onGenerateStarted={onGenerateStarted}
          />
        </Stack>
      </Stack>
    );
  }

  // Case 2 (summary): generation in progress — spinner placeholder
  if (isGenerating) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        py={6}
        mt={2}
      >
        <CircularProgress size={32} />
        <Typography variant="body3" color="text.secondary">
          Generation in progress…
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This may take a moment. The page will update automatically.
        </Typography>
      </Stack>
    );
  }

  // Case 3 (summary): idle — empty state with generate button
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      columnGap={2.5}
      alignItems="center"
      justifyContent="center"
      width="100%"
      minHeight="280px"
      mt={2}
    >
      <Box
        component="img"
        src={NoDataImage}
        alt=""
        sx={{ height: "130px", p: 2 }}
      />
      <Stack rowGap={3}>
        <Typography variant="body3">
          No summary available yet. Click the button to generate.
        </Typography>
        <LoadingButton
          variant="contained"
          onClick={onGenerate}
          loading={isLoading}
          disabled={isLoading}
        >
          Generate Summary
        </LoadingButton>
      </Stack>
    </Stack>
  );
}
